import { memo, useCallback, useMemo } from "react";
import {v4 as uuid} from 'uuid';
import { OptionType, Security } from "../../proto/generated/security";
import { useStyles } from "./styles";
import { Box, Paper } from "@mui/material";
import LivePayoffTargetControlsContainer from "./livePayoffTargetControlsContainer";
import { useSelector } from "react-redux";
import { selectAddOrderEnabled, selectBatchId, selectBasketId, selectOrderType, selectPayoffGraphData, selectStrategyBuilderMode, selectTradeOrders } from "../../selectors/strategyBuilder";
import PayoffGraph from "../payoffGraph/payoffGraph";
import { useDispatch } from "react-redux";
import { setBatchId, TradeOrder, updateTradeOrders } from "../../reducer/strategyBuilder.reducer";
import { Broker_OrderType, Order, TransactionType } from "../../proto/generated/order";
import { selectIsEmbedded } from "../../selectors/device";
import { selectOrdersHistory } from "../../selectors/orders";
import { isOrderBatchComplete } from "../../util/tradepanel";
import { selectUnderlyingOptionsTradeData } from "../../selectors/options";
import useSelectPrimaryOrUnderlyingSecurity from "../../hooks/useSelectPrimaryOrUnderlyingSecurity"
import { tradeOrderReferencePriceFromOption } from "../../util/orderBookPrice";
import SubtleShimmer from "../common/SubtleShimmer";
import StrategyPayoffSkeleton from "../strategyBuilder/StrategyPayoffSkeleton";
import { selectPositions } from "../../selectors/positions";

export type SdData = {
    label: string,
    price: number
}

export type PayoffLeg = {
    id: string,
    security: Security,
    quantity: number,
    type: 'position' | 'order',
    closeQuantity?: number,
    linkedOrders?: string[],
    readonly: boolean,
    entryPrice?: number
}

export type PayoffProfitData = {
    price: number,
    expiryProfit: number, // expiry profit including current position and new trades.
    expectedProfit: number, // expected profit including current position and new trades.
    existingExpiryProfit: number // expiry profit as per current positions
}

export type LivePayoffContainerProps = {
    showTargetControls?: boolean,
    readonly?: boolean
}

const LivePayoffContainer: React.FC<LivePayoffContainerProps> = ( { showTargetControls = true, readonly }) => {

    const {classes} = useStyles();
    const dispatch = useDispatch();
    const { primaryOrUnderlyingToken } = useSelectPrimaryOrUnderlyingSecurity()
    const orderHistory = useSelector(selectOrdersHistory);
    const batchId = useSelector(selectBatchId);

    
    const tradeOrders = useSelector(selectTradeOrders);
    const strategyBuilderMode = useSelector(selectStrategyBuilderMode);
    const basketId = useSelector(selectBasketId);
    const positions = useSelector(selectPositions);
    const builderOrderType = useSelector(selectOrderType);
    const addOrderEnabled = useSelector(selectAddOrderEnabled);
    const isEmbedded = useSelector(selectIsEmbedded);
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(primaryOrUnderlyingToken))
    const { sdData, profitData, optionsTradesData, spotPrice, breakevenPoints, payoffStrikes, payoffLegs } = useSelector(selectPayoffGraphData)

    const basketPositions = useMemo(
        () => positions?.filter((p) => p.basketId === basketId) ?? [],
        [positions, basketId]
    )
    const hasActiveStrategy = useMemo(
        () =>
            (tradeOrders?.length ?? 0) > 0 ||
            basketPositions.length > 0 ||
            ['analyze', 'squareOff'].includes(strategyBuilderMode),
        [tradeOrders?.length, basketPositions.length, strategyBuilderMode]
    )
    const showPayoffShimmer = hasActiveStrategy && (!profitData?.length || !payoffLegs?.length)
    
    const orderHistoryMap = useMemo(() => {
        const map: Record<string, Order> = {};
        return orderHistory.reduce((acc, order) => {
            acc[order.orderId] = order;
            return acc;
        }, map);
    }, [orderHistory]);
    const currentBatchOrders = useMemo(() => 
        tradeOrders.filter(order => !orderHistoryMap[order.clientOrderId] || orderHistoryMap[order.clientOrderId].batchId === batchId )
    , [tradeOrders, orderHistoryMap, batchId]);

    const updateLegStrike = useCallback((legId: string, strike: number) => {
        let newTradeOrders = [...(tradeOrders || [])]
        const leg = payoffLegs.find(l => l.id === legId);
        const optionsTradesData = optionExpiries.find(o=>o.expiration?.expiry?.expiry === leg?.security?.expiry?.expiry)
        if(optionsTradesData) {
            if (leg && leg.type === "order") {
                let order = newTradeOrders.find(t => t.clientOrderId === legId);
                if (order) {
                    const oldSecurity = order.security;
                    const tradeInfo = optionsTradesData.trades.find(t => (t.strikeCol.strike/100) === strike)
                    const orderSecurity = oldSecurity.optionType === OptionType.CE ? tradeInfo.callOption : tradeInfo.putOption;
                    order = {
                        ...order,
                        security: orderSecurity.security
                    }
    
                    if (order.price !== 0) {
                        order.price =  order.transactionType === TransactionType.BUY ? orderSecurity.topAsk : orderSecurity.topBid;
                    }
                    newTradeOrders = newTradeOrders.map((o) => o.clientOrderId === order.clientOrderId ? order : o)
    
                    if (isEmbedded) {
                        callNative('onLegStrikeUpdate', legId, (orderSecurity.security?.strikePrice??0).toString());
                    }

                    // If the leg now sits on top of an opposing order for the same
                    // contract (e.g. dragged back to where an adjustment created its
                    // closing leg), net them out so the redundant legs are removed.
                    const opposingOrder = newTradeOrders.find((o) =>
                        o.clientOrderId !== order.clientOrderId &&
                        o.security.code === order.security.code &&
                        o.security.exchange === order.security.exchange &&
                        o.transactionType !== order.transactionType
                    )
                    if (opposingOrder) {
                        if (opposingOrder.quantity === order.quantity) {
                            newTradeOrders = newTradeOrders.filter((o) => o.clientOrderId !== order.clientOrderId && o.clientOrderId !== opposingOrder.clientOrderId)
                        } else if (opposingOrder.quantity > order.quantity) {
                            newTradeOrders = newTradeOrders
                                .filter((o) => o.clientOrderId !== order.clientOrderId)
                                .map((o) => o.clientOrderId === opposingOrder.clientOrderId ? { ...o, quantity: o.quantity - order.quantity } : o)
                        } else {
                            newTradeOrders = newTradeOrders
                                .filter((o) => o.clientOrderId !== opposingOrder.clientOrderId)
                                .map((o) => o.clientOrderId === order.clientOrderId ? { ...o, quantity: o.quantity - opposingOrder.quantity } : o)
                        }
                    }
                }
                dispatch(updateTradeOrders(newTradeOrders));
            } else if (leg && leg.type === 'position' && (leg.security.strikePrice/(leg.security.priceMultiplier??100)) !== strike) {
                const transactionType = leg.quantity > 0 ? TransactionType.BUY : TransactionType.SELL
                const oldSecurity = leg.security
                const tradeInfo = optionsTradesData.trades.find(t => (t.strikeCol.strike/100) === strike)
                const orderSecurity = oldSecurity.optionType === OptionType.CE ? tradeInfo.callOption : tradeInfo.putOption;
                onAddLegs([
                    {
                        transactionType: transactionType === TransactionType.BUY ? TransactionType.SELL : TransactionType.BUY, 
                        security: oldSecurity,
                        quantity: Math.abs(leg.quantity)
                    }, {
                        transactionType,
                        security: orderSecurity.security,
                        quantity: Math.abs(leg.quantity)
                    }
                ])
            }
        }

    }, [tradeOrders, optionsTradesData, payoffLegs, isEmbedded])

    const onAddLegs = useCallback((orderData: {transactionType: TransactionType, security: Security, quantity?:number} []) => {
        let newOrders = [...tradeOrders]
        orderData.forEach(d=>{
            newOrders = onAddLeg(d.transactionType, d.security, d.quantity, newOrders)
        })
        dispatch(updateTradeOrders(newOrders));
    }, [tradeOrders])

    const onAddLeg = useCallback((transactionType: TransactionType, security: Security, quantity?:number, tradeOrdersPassed?: TradeOrder[]): TradeOrder[] => {
        const optionsTradesData = optionExpiries.find(o=>o.expiration?.expiry?.expiry === security?.expiry?.expiry)
        const tradeData = optionsTradesData?.trades.find(t => t.strikeCol.strike === security.strikePrice)

        if (!tradeData && builderOrderType === Broker_OrderType.LIMIT_ORDER) return [];


        if (isOrderBatchComplete(currentBatchOrders, orderHistoryMap)) {
            console.log("builder: batch complete resetting batch id")
            dispatch(setBatchId(uuid()));
        }
        
        const option = security.optionType === OptionType.CE ? tradeData.callOption : tradeData.putOption;
        const newOrders = [...(tradeOrdersPassed ? tradeOrdersPassed : tradeOrders)]
        newOrders.push({
            clientOrderId: uuid(),
            security,
            quantity: quantity ? quantity : (security.lotSize??1),
            selected: true,
            transactionType,
            price: tradeOrderReferencePriceFromOption(transactionType, option)
        })

        if(!tradeOrdersPassed) dispatch(updateTradeOrders(newOrders));
        return newOrders
    }, [tradeOrders, optionsTradesData, builderOrderType, isEmbedded, currentBatchOrders, orderHistoryMap])

    const onAddLegClick = useCallback((strike: number) => {
        if (isEmbedded) {
            callNative('onAddLeg', strike.toString());
        }
    }, [isEmbedded])

    return <Box className={`${classes.container}`}>
            <Paper className={classes.payoffGraph}>
                {showPayoffShimmer ? (
                    <SubtleShimmer loading fill>
                        <StrategyPayoffSkeleton />
                    </SubtleShimmer>
                ) : (
                    <PayoffGraph profitData={profitData} payoffLegs={payoffLegs} sdData={sdData} spotPrice={spotPrice} optionsTradesData={optionsTradesData} addLegEnabled={addOrderEnabled}
                        breakevenPoints={breakevenPoints} payoffStrikes={payoffStrikes} updateLegStrike={updateLegStrike} onAddLeg={onAddLeg} readonly={readonly} onAddLegClick={onAddLegClick} />
                )}
            </Paper>
            {showTargetControls && (
                <Paper className={classes.targetControls}>
                    <LivePayoffTargetControlsContainer />
                </Paper>)
            }
        </Box>;
}


export default memo(LivePayoffContainer);